const EXTRACTION_API_URL = import.meta.env.VITE_EXTRACTION_API_URL || 'https://extraction.ciobaa.easypanel.host';
const EXTRACTION_API_SECRET = import.meta.env.VITE_EXTRACTION_API_SECRET || '';

interface GeneratePromptParams {
  mode: 'discovery' | 'refine';
  messages: { role: string; content: string }[];
  context: {
    historial_conversaciones?: string;
    productos?: string;
    documentos_negocio?: string;
    current_prompt?: string;
  };
}

interface StreamCallbacks {
  onText: (text: string) => void;
  onDone: () => void;
  onError: (error: string) => void;
}

/**
 * Stream prompt generation from VPS service.
 * Returns a cleanup function to abort the request.
 */
export function streamGeneratePrompt(
  params: GeneratePromptParams,
  callbacks: StreamCallbacks
): () => void {
  const controller = new AbortController();

  (async () => {
    try {
      const response = await fetch(`${EXTRACTION_API_URL}/api/generate-prompt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${EXTRACTION_API_SECRET}`,
        },
        body: JSON.stringify(params),
        signal: controller.signal,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Error del servidor' }));
        callbacks.onError((errorData as any).error || `Error ${response.status}`);
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) {
        callbacks.onError('No response body');
        return;
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed || !trimmed.startsWith('data: ')) continue;

          const data = trimmed.slice(6);
          if (data === '[DONE]') {
            callbacks.onDone();
            return;
          }

          try {
            const parsed = JSON.parse(data);
            if (parsed.type === 'text') {
              callbacks.onText(parsed.content);
            } else if (parsed.type === 'error') {
              callbacks.onError(parsed.content);
            }
          } catch {
            // Skip malformed lines
          }
        }
      }

      callbacks.onDone();
    } catch (error: any) {
      if (error.name === 'AbortError') return;
      callbacks.onError(error.message || 'Error de conexion');
    }
  })();

  return () => controller.abort();
}

// ═══════════════════════════════════════════════════════════════
// V2: Pipeline de generación automática de prompts (3 fases)
// ═══════════════════════════════════════════════════════════════

export interface GenerateV2Context {
  historial_conversaciones?: string;
  productos?: string;
  documentos_negocio?: string;
  prompt_calendario?: string;
}

export interface GenerateV2Analysis {
  business: { name: string; sector: string; [key: string]: any };
  cross_validation?: {
    inconsistencies: Array<{ field: string; source_historial: string; source_documentos: string; severity: string }>;
    missing_critical_info: Array<{ field: string; why_important: string }>;
    clarifying_questions: ClarifyingQuestion[];
  };
  [key: string]: any;
}

export interface ClarifyingQuestion {
  id: string;
  question: string;
  context: string;
  type: 'inconsistency' | 'missing_info' | 'ambiguity' | 'strategic';
  suggested_answer: string | null;
  field_path: string;
}

export interface AnalyzeBusinessResponse {
  success: boolean;
  analysis: GenerateV2Analysis;
  has_questions: boolean;
  questions: ClarifyingQuestion[];
  analysis_time_ms: number;
}

export interface GenerateV2Validation {
  score: number;
  total_lines: number;
  total_chars: number;
  sections_found: number;
  checklist: { passed: string[]; failed: string[] };
  strengths: string[];
  suggestions: string[];
}

export interface GenerateV2Stats {
  total_chars: number;
  total_lines: number;
  generation_time_ms: number;
  score: number;
}

export interface StreamV2Callbacks {
  onPhase: (phase: string, message: string) => void;
  onAnalysis: (data: GenerateV2Analysis) => void;
  onText: (text: string) => void;
  onValidation: (data: GenerateV2Validation) => void;
  onDone: (stats: GenerateV2Stats) => void;
  onError: (error: string) => void;
}

/**
 * Analyze business context (Phase 1) — returns analysis + clarifying questions.
 * This is a regular JSON request, not SSE.
 */
export async function analyzeBusinessContext(
  context: GenerateV2Context,
  agentId?: string,
  signal?: AbortSignal
): Promise<AnalyzeBusinessResponse> {
  const response = await fetch(`${EXTRACTION_API_URL}/api/analyze-business`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${EXTRACTION_API_SECRET}`,
    },
    body: JSON.stringify({ context, agent_id: agentId }),
    signal,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Error del servidor' }));
    throw new Error((errorData as any).error || `Error ${response.status}`);
  }

  return response.json();
}

/**
 * Stream prompt generation V2 (generation + validation phases).
 * Accepts optional pre-computed analysis and user answers.
 * Returns a cleanup function to abort the request.
 */
export function streamGeneratePromptV2(
  context: GenerateV2Context,
  callbacks: StreamV2Callbacks,
  options?: {
    analysis?: GenerateV2Analysis;
    userAnswers?: Array<{ question_id: string; answer: string }>;
    agentId?: string;
  }
): () => void {
  const controller = new AbortController();

  (async () => {
    try {
      const body: Record<string, any> = {};
      if (options?.analysis) {
        body.analysis = options.analysis;
      } else {
        body.context = context;
      }
      if (options?.userAnswers?.length) {
        body.user_answers = options.userAnswers;
      }
      if (options?.agentId) {
        body.agent_id = options.agentId;
      }

      const response = await fetch(`${EXTRACTION_API_URL}/api/generate-prompt-v2`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${EXTRACTION_API_SECRET}`,
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Error del servidor' }));
        callbacks.onError((errorData as any).error || `Error ${response.status}`);
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) {
        callbacks.onError('No response body');
        return;
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed || !trimmed.startsWith('data: ')) continue;

          const data = trimmed.slice(6);
          if (data === '[DONE]') {
            return;
          }

          try {
            const parsed = JSON.parse(data);
            switch (parsed.type) {
              case 'phase':
                callbacks.onPhase(parsed.phase, parsed.message);
                break;
              case 'analysis':
                callbacks.onAnalysis(parsed.data);
                break;
              case 'text':
                callbacks.onText(parsed.content);
                break;
              case 'validation':
                callbacks.onValidation(parsed.data);
                break;
              case 'done':
                callbacks.onDone(parsed.stats);
                break;
              case 'error':
                callbacks.onError(parsed.message || parsed.content);
                break;
            }
          } catch {
            // Skip malformed lines
          }
        }
      }
    } catch (error: any) {
      if (error.name === 'AbortError') return;
      callbacks.onError(error.message || 'Error de conexión');
    }
  })();

  return () => controller.abort();
}

/**
 * Stream calendar sub-agent prompt generation.
 * Returns a cleanup function to abort the request.
 */
export function streamGenerateCalendarPrompt(
  analysis: GenerateV2Analysis,
  callbacks: {
    onText: (text: string) => void;
    onDone: (stats: { total_chars: number; total_lines: number; generation_time_ms: number }) => void;
    onError: (error: string) => void;
  },
  agentId?: string,
  calendarAnswers?: Record<string, string>
): () => void {
  const controller = new AbortController();

  (async () => {
    try {
      const response = await fetch(`${EXTRACTION_API_URL}/api/generate-calendar-prompt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${EXTRACTION_API_SECRET}`,
        },
        body: JSON.stringify({ analysis, agent_id: agentId, calendar_answers: calendarAnswers }),
        signal: controller.signal,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Error del servidor' }));
        callbacks.onError((errorData as any).error || `Error ${response.status}`);
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) {
        callbacks.onError('No response body');
        return;
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed || !trimmed.startsWith('data: ')) continue;

          const data = trimmed.slice(6);
          if (data === '[DONE]') return;

          try {
            const parsed = JSON.parse(data);
            if (parsed.type === 'text') {
              callbacks.onText(parsed.content);
            } else if (parsed.type === 'done') {
              callbacks.onDone(parsed.stats);
            } else if (parsed.type === 'error') {
              callbacks.onError(parsed.content || parsed.message);
            }
          } catch {
            // Skip malformed lines
          }
        }
      }
    } catch (error: any) {
      if (error.name === 'AbortError') return;
      callbacks.onError(error.message || 'Error de conexión');
    }
  })();

  return () => controller.abort();
}

/**
 * Process a document file through the VPS service.
 */
export async function processDocument(params: {
  fileUrl: string;
  fileType: string;
}): Promise<{
  success: boolean;
  extracted_text: string;
  summary: string;
  char_count: number;
}> {
  const response = await fetch(`${EXTRACTION_API_URL}/api/process-document`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${EXTRACTION_API_SECRET}`,
    },
    body: JSON.stringify({
      file_url: params.fileUrl,
      file_type: params.fileType,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Error del servidor' }));
    throw new Error((errorData as any).error || `Error ${response.status}`);
  }

  return response.json();
}
